import { loadPipeline } from 'js/actions';
import PopUpForm from 'js/components/elements/pop-up-form/PopUpForm';
import useAppDispatch from 'js/hooks/useAppDispatch';
import UIUtils from 'js/lib/UIUtils';
import { find } from 'lodash';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import useLLMProcessor from 'new/elements/flows/ModalConfigureLLM/useLLMProcessor';
import { useForm } from 'new/elements/Form/useForm';
import { Stack } from 'new/elements/Stack';
import useEntities from 'new/state/entities/repository/useEntities';
import usePaging from 'new/state/paging/repository/usePaging';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classes from './ConfigStep.module.scss';
import useSinksAdapter from 'new/state/sinks/adapter/useSinksAdapter';
import { getDefaultSelectionId, sortByDisplayOrder } from 'new/utils/displayOrder';

interface ConfigStepProps {
  setOpen: (open: boolean) => void;
  data?: any;
}

const chipStyles = {
  height: '32px',
  border: 'none',
  padding: 0,
};

export const ConfigStep = ({ setOpen, data }: ConfigStepProps) => {
  const dispatch = useAppDispatch();
  const {
    selectedConnection,
    updateFormData,
    sink_config: sinkConfig,
    dataSetId,
    createSink,
    updateSink,
  } = useLLMProcessor();
  const { activate } = useSinksAdapter();
  const { getMetaInfoById } = usePaging();
  const { data: vendorEndpoints } = useEntities('vendorEndpoints');
  const { overrideNextFunction, setConfirmButtonTitle, setCancelButtonTitle } = useForm();
  const fields = useSelector((state: any) => state.paging.llmConfigById[selectedConnection]) || [];
  const flowId = useSelector((state: any) => state.entities.dataSets[dataSetId]?.origin_node_id);
  const meta = getMetaInfoById(selectedConnection);

  const nonEndpointFields = useMemo(
    () => fields.filter((field) => field.name !== 'vendor_endpoint_id'),
    [fields],
  );

  useEffect(() => {
    const endpointField = find(fields, {
      name: 'vendor_endpoint_id',
    });

    const endpoints = sortByDisplayOrder(
      Object.values(vendorEndpoints).filter((endpoint: any) => {
        if (!endpointField) return false;
        return (
          endpointField?.options.find((v) => v.value === endpoint.id) && endpoint?.config?.isLLM
        );
      }) as any[],
    );
    // Keep a saved endpoint that is still valid for this connection; default only new selections.
    // Until the connector's endpoints have loaded, leave the current value untouched so a saved
    // endpoint isn't cleared and then replaced by the first one once they arrive.
    const savedEndpointId = sinkConfig?.vendor_endpoint_id;
    let newSinkConfig = {
      ...sinkConfig,
      vendor_endpoint_id: endpoints.length
        ? getDefaultSelectionId(endpoints, savedEndpointId)
        : savedEndpointId,
    };
    newSinkConfig = UIUtils.configWithMissingDefaults(newSinkConfig, fields);

    updateFormData({
      sink_config: newSinkConfig,
    });
  }, [selectedConnection, fields?.length]);

  useEffect(() => {
    setConfirmButtonTitle(data?.id ? 'Save' : 'Create');
    setCancelButtonTitle('Cancel');

    return () => {
      setConfirmButtonTitle(undefined);
      setCancelButtonTitle(undefined);
    };
  }, [data?.id]);

  const onClickNextStep = async () => {
    let createdOrUpdatedSink;
    if (data?.id) {
      createdOrUpdatedSink = await updateSink({
        id: data?.id,
        vendor_endpoint_id: sinkConfig.vendor_endpoint_id,
        template_config: sinkConfig,
      });
    } else {
      createdOrUpdatedSink = await createSink({
        name: 'LLM Processor',
        data_set_id: dataSetId,
        vendor_endpoint_id: sinkConfig.vendor_endpoint_id,
        template_config: sinkConfig,
      });
    }

    if (createdOrUpdatedSink) {
      dispatch(loadPipeline(flowId));
      setOpen(false);
      if (createdOrUpdatedSink?.status !== 'ACTIVE') {
        await activate(createdOrUpdatedSink?.id);
      }
      return Boolean(createdOrUpdatedSink.id);
    }
  };

  useEffect(() => {
    overrideNextFunction(onClickNextStep);
  }, [onClickNextStep]);

  const onChange = (name, value) => {
    updateFormData({
      sink_config: {
        ...sinkConfig,
        [name]: value,
      },
    });
  };

  return (
    <Stack>
      <Stack className={classes.headerConnection}>
        <div className={classes.connectionBox}>
          <ConnectionTypeChip icon={meta.iconSm} label={meta.name} style={chipStyles} />
        </div>
      </Stack>
      <Stack className={classes.content}>
        <PopUpForm fields={nonEndpointFields} data={sinkConfig} onFieldUpdated={onChange} />
      </Stack>
    </Stack>
  );
};

ConfigStep.formConfig = {
  title: 'Config',
};
